import { Match, Show, Switch, createSignal, useContext } from 'solid-js';
import { AppContext } from '../../../app-context-provider/app-context-provider';
import { Section } from '../../../grid-system/section/section';
import { User } from '../../../types/shared';
import { Button } from '../../../ui-components/button/button';
import { StyledVerticalSpace } from '../../../ui-components/utility-style-components/spacing';
import { AtosLogo } from '../../icons-library/atos-logo-desktop';
import { CrossIcon } from '../../icons-library/cross';
import { StyledCloseButton, StyledCloseButtonText } from '../../menu/menu-desktop/menu-header/menu-header.styles';
import { StyledLogo, StyledLogoContent } from '../../menu/menu-desktop/shortcuts/shortcuts.styles';
import { UserOrderHistory } from '../../order-history/user-order-history';
import { TabHandler } from '../../menu/menu-desktop/tab-handling';
import { AccountEducationContent } from '../../account-education-content/account-education-content';
import { 
    StyledLoginMenuSignedInAsContent,
    StyledSidebarNavigationSection,
    StyledNavButton,
    StyledUserMenuGrid,
    StyledNavItem,
    StyledContentWrapper,
    StyledBottomFade,
    StyledLeftArea,
    StyledLoginMenuHeader,
    StyledLoginMenuLeftBackgroundPanel,
    StyledLoginMenuRightArea,
    StyledLoginMenuSignedInAs,
    StyledLoginMenuSignedInAsBold,
    StyledSpacer,
    StyledNavigationSectionHeader,
    StyledNavigation,
} from './user-profile-menu-desktop.styles';
import { MyContent } from '../../my-content/my-content';
import { Link as SolidLink, useNavigate } from '@solidjs/router';
import { Text } from '../../../ui-components/text/text';
import { SmallCertificateIcon } from '../../icons-library/small-certificate';
import { StarIcon } from '../../icons-library/star';
import { ArrowRightIcon } from '../../icons-library/arrow-right';
import theme from '../../../style/theme';


export const UserProfileMenuDesktop = (props: LoginMenuProps) => {
    const {
        localize,
        siteInfo,
        viewport,
        coursesListingPage,
        pdfBuilderPage,
        translations,
    } = useContext(AppContext);

    const navigate = useNavigate();

    const isTablet = () => viewport.width <= theme.breakpoints.ORIGINAL_TABLET;

    const [ activeView, setActiveView ] = createSignal(siteInfo.siteType === 'atos-care' 
        ? 'orders'
        : coursesListingPage.url ? 'learning' : 'bookmarks'
    );

    // Accessibility/keyboard-controls related
    const [ firstTabbableElement, setFirstTabbableElement ] = createSignal<HTMLElement | undefined>();
    const [ lastTabbableElement, setLastTabbableElement ] = createSignal<HTMLElement | undefined>();

    const SignedInStatus = () => (
        <StyledLoginMenuSignedInAsContent>
            <StyledLoginMenuSignedInAs title={props.user?.email}>
                <StyledLoginMenuSignedInAsBold>
                    {localize('signed-in-as', 'Signed in as')}
                </StyledLoginMenuSignedInAsBold>
                <div>{props.user?.firstName || props.user?.email || ''}</div>
            </StyledLoginMenuSignedInAs>
            <StyledVerticalSpace size={viewport.width < 920 ? 0.75 : 1.2} />
            <Button
                label={localize('sign-out', 'Sign out')}
                onClick={() => props.handleSignOutClick()}
                small={ viewport.width < 920 }
            />
        </StyledLoginMenuSignedInAsContent>
    );

    const Header = () => (
        <StyledLoginMenuHeader>
            <StyledSpacer />
            <StyledCloseButton
                onClick={() => props.handleCloseMenuClick()}
                aria-label={localize('close', 'Close')}
            >
                <CrossIcon fill="white" width={1.79} height={1.79} ariaHidden />
                <StyledCloseButtonText>{localize('close', 'Close')}</StyledCloseButtonText>
            </StyledCloseButton>
        </StyledLoginMenuHeader>
    );

    const Logo = () => (
        <StyledLogoContent>
            <SolidLink href="/" ref={(el: HTMLElement) => {
                setFirstTabbableElement(el);
            }}>
                <StyledLogo>
                    <AtosLogo />
                </StyledLogo>
            </SolidLink>
        </StyledLogoContent>
    );

    const Navigation = () => (
        <StyledNavigation>
            <StyledSidebarNavigationSection>
                <StyledNavigationSectionHeader>
                    {`${localize('my-content', 'My content')}:`}
                </StyledNavigationSectionHeader>

                <Show when={coursesListingPage.url}>
                    <StyledNavItem>
                        <StyledNavButton
                            onClick={() => setActiveView('learning')}
                            active={activeView() === 'learning'}
                        >
                            <SmallCertificateIcon width={isTablet() ? 1.5 : 2} height={isTablet() ? 1.5 : 2} fill='#fff' />
                            <span>{localize('e-learning', 'E-learning')}</span>
                        </StyledNavButton>
                    </StyledNavItem>
                </Show>

                <StyledNavItem>
                    <StyledNavButton
                        onClick={() => setActiveView('bookmarks')}
                        active={activeView() === 'bookmarks'}
                    >
                        <StarIcon width={isTablet() ? 1.5 : 2} height={isTablet() ? 1.5 : 2} fill='#fff' ariaHidden />
                        <span>{localize('bookmarks', 'Bookmarks')}</span>
                    </StyledNavButton>
                </StyledNavItem>
            </StyledSidebarNavigationSection>

            <Show when={pdfBuilderPage.url}>
                <StyledSidebarNavigationSection>
                    <StyledNavigationSectionHeader>
                        <Text
                            fontSize='large'
                            color={'white'}
                            displayRedVerticalLine={false}
                        >{`${localize('profile-tools', 'Tools')}:`}</Text>
                    </StyledNavigationSectionHeader>

                    <StyledNavItem>
                        <StyledNavButton onClick={() => {
                            navigate(pdfBuilderPage.url);
                            props.handleCloseMenuClick();
                        }}>
                            <ArrowRightIcon width={1.5} height={1.5} fill='#fff' />
                            <span>{translations['profile-pdf-builder-heading'] || pdfBuilderPage.title}</span>
                        </StyledNavButton>
                    </StyledNavItem>
                </StyledSidebarNavigationSection>
            </Show>
        </StyledNavigation>
    );

    return (
        <Section
            templateShorthand={[ 12 ]}
            widthType={'bgFull'}
            removeSpacingBlock={true}
            heightType="fill"
            customCss="padding-top: 0 !important; padding-bottom: 0 !important;"
        >
            <TabHandler
                refs={{
                    firstTabbableElement,
                    lastTabbableElement,
                }}
            >
                <StyledUserMenuGrid>
                    <StyledLeftArea>
                        <Logo />

                        <Show when={siteInfo.siteType === 'default'}>
                            <Navigation />
                        </Show>

                        <SignedInStatus />

                        <StyledLoginMenuLeftBackgroundPanel />
                    </StyledLeftArea>

                    <StyledLoginMenuRightArea>
                        <Header />

                        <StyledContentWrapper>
                            <Switch>
                                <Match when={activeView() === 'bookmarks'}>
                                    <MyContent lastElement={(el: HTMLElement) => setLastTabbableElement(el)} />
                                </Match>
                                <Match when={activeView() === 'learning'}>
                                    <AccountEducationContent lastElement={(el: HTMLElement) => setLastTabbableElement(el)}/>
                                </Match>
                                <Match when={activeView() === 'orders'}>
                                    <UserOrderHistory lastElement={(el: HTMLElement) => setLastTabbableElement(el)}/>
                                </Match>
                            </Switch>
                        </StyledContentWrapper>

                        <StyledBottomFade />

                    </StyledLoginMenuRightArea>
                </StyledUserMenuGrid>
            </TabHandler>
        </Section>
    );
};
export type LoginMenuProps = {
    handleCloseMenuClick: () => void;
    handleSignOutClick: () => void;
    user: User | null;
};
